import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import {
  BarChart2,
  Briefcase,
  CalendarClock,
  CheckCircle,
  Cpu,
  DollarSign,
  MessageSquare,
  Phone,
  User,
  Zap
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { submitEnterpriseSalesForm } from "../store/enterpriseSales/enterpriseSalesActions";
import { Formik } from "formik";
import * as Yup from "yup";

// Enterprise Validation schema
const enterpriseValidationSchema = Yup.object().shape({
  fullName: Yup.string()
    .trim()
    .required("Full name is required")
    .min(3, "Full name must be at least 3 characters")
    .max(50, "Full name can not be more than 50 characters"),
  companyName: Yup.string()
    .trim()
    .min(2, "Company name must be at least 3 characters")
    .max(50, "Company name can not be more than 50 characters")
    .required("Company name is required"),
  email: Yup.string()
    .trim()
    .email("Invalid email address")
    .required("Email is required"),
  phoneNumber: Yup.string()
    .trim()
    .matches(/^\+?[0-9]{10,15}$/, "Phone number must be 10-15 digits"),
  inventoryVolume: Yup.string().required("Inventory volume is required"),
  message: Yup.string()
    .trim()
    .max(500, "Message cannot exceed 500 characters")
    .required("Message is required")
});

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
};

const itemVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, delay: 0.1 } }
};

const EnterpriseSalesPage = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { loading } = useSelector((state) => state.enterpriseSales);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      await dispatch(submitEnterpriseSalesForm(values)).unwrap();
      toast({
        title: "Inquiry Sent!",
        description:
          "Our Enterprise Sales team will contact you within 24 hours. Thank you!",
        variant: "default"
      });
      resetForm();
    } catch (error) {
      console.error("Error submitting enterprise contact form:", error);
    }
  };

  const enterpriseFeatures = [
    { icon: DollarSign, text: "Custom pricing for large inventories" },
    { icon: Zap, text: "Top placement in search results" },
    { icon: User, text: "Dedicated account manager" },
    { icon: Cpu, text: "API access for bulk uploads" },
    { icon: CheckCircle, text: "Early feature access" },
    { icon: BarChart2, text: "Premium reporting tools" },
    { icon: Briefcase, text: "System integration support (POS/ERP)" }
  ];

  const calendlyLink =
    "https://calendly.com/your-username/enterprise-consultation";

  const initialValues = {
    fullName: "",
    companyName: "",
    email: "",
    phoneNumber: "",
    inventoryVolume: "",
    message: ""
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen"
    >
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary/80 via-primary to-purple-600 text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.h1
            variants={itemVariants}
            className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6"
          >
            Ready to Scale? Let’s Build a Custom Plan for Your Business
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto text-primary-foreground/90"
          >
            High-volume seller? National distributor? Let’s talk about tailored
            pricing, premium visibility, and dedicated support to grow your
            presence on PartsLokator.
          </motion.p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 md:py-20 space-y-8 sm:space-y-16 md:space-y-24">
        <motion.section variants={itemVariants}>
          <h2 className="text-[22px] sm:text-3xl md:text-4xl font-bold text-center mb-6 md:mb-12 bg-gradient-to-r from-primary via-purple-500 to-accent text-transparent bg-clip-text">
            Unlock Enterprise Benefits with PartsLokator
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {enterpriseFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="flex items-start p-6 bg-card border border-border/20 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <feature.icon className="size-5 md:h-8 md:w-8 text-primary mr-4 flex-shrink-0 mt-1" />
                <p className="text-sm md:text-base text-md text-foreground">
                  {feature.text}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section variants={itemVariants} className="max-w-3xl mx-auto">
          <h2 className="text-[22px] sm:text-3xl md:text-4xl font-bold text-center mb-5 md:mb-10 bg-gradient-to-r from-accent via-pink-500 to-red-500 text-transparent bg-clip-text">
            Tell Us About Your Business
          </h2>
          <Formik
            initialValues={initialValues}
            validationSchema={enterpriseValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              handleChange,
              handleBlur,
              isSubmitting,
              handleSubmit,
              errors,
              touched,
              setFieldTouched,
              setFieldValue
            }) => (
              <form
                onSubmit={handleSubmit}
                className="space-y-6 bg-card p-5 sm:p-8 md:p-10 rounded-2xl shadow-2xl border border-border/20"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <Label
                      htmlFor="fullName"
                      className="text-sm font-medium text-foreground/80 flex items-center mb-1"
                    >
                      <User className="w-4 h-4 mr-2 text-primary" /> Full Name{" "}
                      <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      placeholder="Your Full Name"
                      value={values.fullName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="bg-background/50 border-border/30 focus:border-primary"
                    />
                    <AnimatePresence>
                      {errors?.fullName && touched?.fullName && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-xs text-red-400"
                        >
                          {errors?.fullName}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="space-y-1">
                    <Label
                      htmlFor="companyName"
                      className="text-sm font-medium text-foreground/80 flex items-center mb-1"
                    >
                      <Briefcase className="w-4 h-4 mr-2 text-primary" />{" "}
                      Company Name <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      type="text"
                      placeholder="Your Company's Name"
                      value={values.companyName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="bg-background/50 border-border/30 focus:border-primary"
                    />
                    <AnimatePresence>
                      {errors?.companyName && touched?.companyName && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-xs text-red-400"
                        >
                          {errors?.companyName}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="email"
                    className="text-sm font-medium text-foreground/80 flex items-center mb-1"
                  >
                    <User className="w-4 h-4 mr-2 text-primary" /> Email Address{" "}
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="bg-background/50 border-border/30 focus:border-primary"
                    title="Enter a valid email address (e.g., name@example.com)"
                  />
                  <AnimatePresence>
                    {errors?.email && touched?.email && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-xs text-red-400"
                      >
                        {errors?.email}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="phoneNumber"
                    className="text-sm font-medium text-foreground/80 flex items-center mb-1"
                  >
                    <Phone className="w-4 h-4 mr-2 text-primary" /> Phone Number
                    (Optional)
                  </Label>
                  <Input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    placeholder="Your Phone Number"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="bg-background/50 border-border/30 focus:border-primary"
                  />
                  <AnimatePresence>
                    {errors?.phoneNumber && touched?.phoneNumber && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-xs text-red-400"
                      >
                        {errors?.phoneNumber}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="inventoryVolume"
                    className="text-sm font-medium text-foreground/80 flex items-center mb-1"
                  >
                    <BarChart2 className="w-4 h-4 mr-2 text-primary" />{" "}
                    Estimated Monthly Inventory Volume{" "}
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Select
                    name="inventoryVolume"
                    value={values.inventoryVolume}
                    onValueChange={(val) =>
                      setFieldValue("inventoryVolume", val)
                    }
                    onOpenChange={(open) => {
                      if (!open) {
                        setFieldTouched("inventoryVolume", true); // mark touched on close
                      }
                    }}
                    // <Select
                    //   name="inventoryVolume"
                    //   onValueChange={handleSelectChange}
                    //   onBlur={handleBlur}
                    //   value={values.inventoryVolume}
                    //   required
                  >
                    <SelectTrigger className="w-full bg-background/50 border-border/30 focus:border-primary">
                      <SelectValue placeholder="Select volume" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-50">1-50 listings</SelectItem>
                      <SelectItem value="51-200">51-200 listings</SelectItem>
                      <SelectItem value="200+">200+ listings</SelectItem>
                      <SelectItem value="custom">
                        Custom/High Volume (specify in message)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <AnimatePresence>
                    {errors?.inventoryVolume && touched?.inventoryVolume && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-xs text-red-400"
                      >
                        {errors?.inventoryVolume}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="message"
                    className="text-sm font-medium text-foreground/80 flex items-center mb-1"
                  >
                    <MessageSquare className="w-4 h-4 mr-2 text-primary" />{" "}
                    Message <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your business needs, goals, or any specific questions..."
                    value={values.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={5}
                    className="bg-background/50 border-border/30 focus:border-primary"
                  />
                  <AnimatePresence>
                    {errors?.message && touched?.message && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-xs text-red-400"
                      >
                        {errors?.message}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 text-xs sm:text-sm lg:text-base transition-all duration-300 ease-in-out transform hover:scale-105 h-auto"
                  disabled={isSubmitting || loading}
                >
                  {isSubmitting || loading
                    ? "Submitting..."
                    : "Submit – Our Team Will Contact You Within 24 Hours"}
                </Button>
              </form>
            )}
          </Formik>
        </motion.section>

        <motion.section variants={itemVariants} className="text-center">
          <h2 className="text-[22px] sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-8 bg-gradient-to-r from-green-400 via-cyan-500 to-blue-600 text-transparent bg-clip-text">
            Prefer a Direct Chat?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto text-xs sm:text-sm md:text-base">
            Schedule a discovery call with our Enterprise Sales Team at your
            convenience.
          </p>
          <Button
            size="lg"
            className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-2 xs:px-4 md:px-8 text-xs sm:text-sm md:text-base lg:text-lg transition-all duration-300 ease-in-out transform hover:scale-105"
            onClick={() => window.open(calendlyLink, "_blank")}
          >
            <CalendarClock className="size-4 md:w-5 md:h-5 mr-2" />
            Schedule a Call With Our Sales Team
          </Button>
        </motion.section>
      </div>
    </motion.div>
  );
};

export default EnterpriseSalesPage;
